import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, UploadCloud } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { ACCEPT, MAX_BYTES } from "@/lib/enhanceApi";
import { uploadClip } from "@/lib/myVoicesApi";
import { safeApiError } from "@/lib/safeErrorText";

const OK = /\.(wav|mp3|m4a|aac|flac|aiff?|ogg)$/i;

/** Upload one or more training recordings, one after another. */
export default function UploadClipDialog({ open, onClose, voiceId, onAdded }) {
  const input = useRef(null);
  const [files, setFiles] = useState([]);
  const [isolate, setIsolate] = useState(false);
  const [at, setAt] = useState(null);
  const pick = (list) => {
    const good = [...list].filter((f) => OK.test(f.name) && f.size <= MAX_BYTES);
    if (good.length < list.length) toast.error("Some files were skipped — use WAV, MP3, M4A, AAC, FLAC, AIFF or OGG under 300 MB.");
    setFiles(good);
  };
  const go = async () => {
    for (let i = 0; i < files.length; i += 1) {
      setAt({ i, p: 0 });
      try { await uploadClip(voiceId, files[i], isolate, (p) => setAt({ i, p })); onAdded(); }
      catch (e) { toast.error(`${files[i].name} didn't upload`, { description: safeApiError(e) }); }
    }
    toast.success("Added — cleaning them up now");
    setAt(null); setFiles([]); onClose();
  };
  const busy = at !== null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent data-testid="upload-clip-dialog" className="max-w-md border-white/10 bg-[#0b0e1c] text-white">
        <DialogHeader>
          <DialogTitle className="font-display">Upload training recordings</DialogTitle>
          <DialogDescription className="text-white/60">Only you, singing — no backing music, no reverb or effects if you can help it.</DialogDescription>
        </DialogHeader>
        <button type="button" data-testid="upload-clip-pick" disabled={busy} onClick={() => input.current?.click()}
          className="mt-2 flex w-full flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-white/15 bg-black/30 px-4 py-8 text-center hover:border-white/30">
          <UploadCloud className="h-8 w-8 text-cyan-300" />
          <span data-testid="upload-clip-files" className="text-[14px] font-medium">{files.length ? `${files.length} file${files.length > 1 ? "s" : ""} chosen` : "Choose recordings"}</span>
          <span className="text-[11px] text-white/45">{files.length ? files.map((f) => f.name).join(", ").slice(0, 120) : "WAV, MP3, M4A… up to 15 minutes each"}</span>
        </button>
        <input ref={input} type="file" multiple accept={ACCEPT} className="hidden" data-testid="upload-clip-input" onChange={(e) => pick(e.target.files || [])} />
        <label className="mt-3 flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-black/30 px-4 py-3">
          <span><span className="block text-[13px] font-medium">Remove the music first</span><span className="block text-[11px] text-white/45">Only if there's a beat behind your voice. Uses AI credits.</span></span>
          <Switch data-testid="upload-clip-isolate" checked={isolate} disabled={busy} onCheckedChange={setIsolate} />
        </label>
        {busy ? (
          <div className="mt-4" data-testid="upload-clip-progress">
            <Progress value={Math.round(at.p * 100)} className="h-2 bg-white/10" />
            <p className="mt-2 text-[12px] text-white/55">Uploading {at.i + 1} of {files.length}… {Math.round(at.p * 100)}%</p>
          </div>
        ) : (
          <button type="button" data-testid="upload-clip-submit" disabled={!files.length} onClick={go}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cyan-300 py-2.5 text-[13px] font-semibold text-black disabled:opacity-40">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Upload and clean
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
